import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const COLOR_TEAL = [26, 118, 126];
const COLOR_GRID = [0, 0, 0];
const COLOR_SECTION = [60, 160, 166];
const FORM_TOP_Y = 38;
const AUDIT_PLAN_FOOTER_CODE = 'FAPP-05-04-QMS';
const TABLE_GRID_STYLES = {
  theme: 'grid',
  styles: {
    fontSize: 7.5,
    cellPadding: 1.2,
    lineColor: COLOR_GRID,
    lineWidth: 0.12,
    textColor: 0,
    valign: 'top',
  },
  headStyles: {
    fillColor: [255, 255, 255],
    textColor: 0,
    lineColor: COLOR_GRID,
    lineWidth: 0.12,
    fontStyle: 'bold',
  },
};
const AUDIT_PLAN_DEFAULT_REQUESTS = [
  'Let us know if we should include any other additional processes or activities in this audit plan',
  'Notify the Lead Auditor of any significant organizational / management system changes since the last audit',
  'Let us know any issue related to the health and safety of the audit teams, including any eventual personal protective equipment required',
];
const AUDIT_PLAN_DEFAULT_NOTES = [
  'Audit Times and sequence may be adjusted in the light of findings and audit needs',
  'Process Owners will be completed and/or confirmed during the opening meeting',
  'Audit of all area could include Objectives, Improvement, Data collecting, Infrastructure and Work Environment, as appropriate.',
  "The company is requested to provide guides to accompany the auditor during the visit. The guide should be authorized to confirm audit findings on behalf of the company.",
  "The company is requested to make available a suitable workspace for the auditor's administration.",
  'Use of Mark, verification of effectiveness of corrective actions from last audit, treatment of complaint and risks/changes will be included during the audit.',
];

// Lead Auditor Recommendation text map (FAPP-05)
const LEAD_AUDITOR_RECOMMENDATION_MAP = {
  rec1: 'The management system is in conformity with the audit criteria and can be considered effective in assuring that objectives will be met. Therefore, certification is recommended.',
  rec2: 'The nonconformity(ies) identified do not jeopardize the certification of the management system. Certification is therefore recommended pending acceptance of the corrective action plan(s) for identified nonconformity(ies).',
  rec3: 'Due to major nonconformity(ies) and/or the total number of minor non-conforms reported, the conformity of the management system with the audit criteria is potentially in jeopardy. As a result, a subsequent On-Site follow-up audit is recommended.',
  rec4: 'Certification is not recommended until effective correction and corrective action have been verified.',
  rec5: 'The audit result requires certification decision review before recommendation can be finalized.',
};
const imageDataCache = new Map();

const FORM_TITLES = {
  'FAPP-01': 'AUDIT PLAN',
  'FAPP-02': 'THREE YEARS AUDIT PROGRAMME',
  'FAPP-03': 'AUDIT ATTENDANCE LIST',
  'FAPP-04': 'AUDIT NOTES',
  'FAPP-05': 'AUDIT REPORT',
  'FAPP-06': 'CERTIFICATE INFORMATION FORM',
  'FAPP-07': 'FINDING FORM',
  'FAPP-07-ACCEPTED': 'FINDING FORM',
  'FAPP-08': 'CHANGE NOTICE FORM',
  'FTRD-01': 'TECHNICAL & CERTIFICATION REVIEW',
  'FTRD-02': 'CERTIFICATE REVIEW & PRODUCTION',
  'FSCP-03': 'AUDIT CONFIRMATION LETTER',
  SUMMARY: 'AUDIT SUMMARY',
};

const FORM_FOOTER_CODES = {
  'FAPP-01': AUDIT_PLAN_FOOTER_CODE,
  'FAPP-02': 'FAPP-02',
  'FAPP-03': 'FAPP-03',
  'FAPP-04': 'FAPP-04',
  'FAPP-05': 'FAPP-05',
  'FAPP-06': 'FAPP-06',
  'FAPP-07': 'FAPP-07',
  'FAPP-07-ACCEPTED': 'FAPP-07',
  'FAPP-08': 'FAPP-08',
  'FTRD-01': 'FTRD-01',
  'FTRD-02': 'FTRD-02',
  'FSCP-03': 'FSCP-03',
};

const LANDSCAPE_CODES = new Set(['SUMMARY', 'FAPP-07', 'FAPP-07-ACCEPTED']);

// Native date format helpers (no date-fns dependency)
const fmtDateNative = (v) => {
  if (!v) return '-';
  try {
    const d = new Date(v);
    if (isNaN(d.getTime())) return String(v);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getMonth()];
    return `${dd} ${mm} ${d.getFullYear()}`;
  } catch { return String(v); }
};


const fmtDateForFile = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const d = (v) => {
  if (v === null || v === undefined || v === '') return '-';
  if (Array.isArray(v)) return v.map((x) => (typeof x === 'object' ? JSON.stringify(x) : String(x))).join(', ');
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
};

const fmtDate = fmtDateNative;

const firstValue = (...values) => {
  for (const value of values) {
    if (value !== null && value !== undefined && value !== '') return value;
  }
  return '';
};

const auditQDocOptions = (code) => {
  if (code === 'FAPP-07' || code === 'FAPP-07-ACCEPTED') {
    return { unit: 'mm', format: 'a3', orientation: 'landscape' };
  }
  if (code === 'SUMMARY') {
    return { unit: 'mm', format: [254, 142.875], orientation: 'landscape' };
  }
  return { unit: 'mm', format: 'a4', orientation: LANDSCAPE_CODES.has(code) ? 'landscape' : 'portrait' };
};

const getClientName = (f = {}, projectData = {}) =>
  firstValue(f.clientName, f.clientCompany, f.companyName, f.clientCompanyName, projectData.clientName, projectData.companyName);

const getClientId = (f = {}, projectData = {}) =>
  firstValue(f.clientId, f.clientID, f.client_id, projectData.clientId, projectData.companyId);

const getAuditCriteria = (f = {}, projectData = {}) =>
  firstValue(f.auditCriteria, f.applicableStandards, projectData.auditCriteria, projectData.standards);

const getAuditActivity = (f = {}, projectData = {}) =>
  firstValue(f.auditActivity, f.activity, f.auditType, projectData.auditType, projectData.auditActivity);

const getAuditDates = (f = {}, projectData = {}) =>
  firstValue(
    f.dateOfActivity,
    f.auditDates,
    f.acknowledgeDateOfAudit,
    formatAuditPlanDateList(f),
    projectData.auditDates,
    projectData.estimatedAuditDate,
  );

const getLeadAuditorName = (f = {}, projectData = {}) =>
  firstValue(f.leadAuditorName, f.leadAuditor, f.auditorName, f.reviewer, projectData.leadAuditorName);

const loadImageAsDataUrl = async (url) => {
  if (imageDataCache.has(url)) return imageDataCache.get(url);
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Failed to load image: ${url}`);
  const blob = await response.blob();
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
  imageDataCache.set(url, dataUrl);
  return dataUrl;
};

const splitLines = (value, fallback = []) => {
  const rows = String(value || '')
    .split(/\r?\n/)
    .map((item) => item.replace(/^\s*[-*•]\s*/, '').replace(/^\s*\d+\.\s*/, '').trim())
    .filter(Boolean);
  return rows.length > 0 ? rows : fallback;
};

const dateToIso = (value) => {
  if (!value) return '';
  const parsed = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toISOString().slice(0, 10);
};

const formatIsoLikeDate = (value) => dateToIso(value) || d(value);

const formatAuditPlanDate = (value) => fmtDateNative(value).replace(/^0/, '');

const formatWeekday = (value) => {
  const normalized = dateToIso(value);
  if (!normalized) return '';
  const date = new Date(`${normalized}T00:00:00`);
  return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][date.getDay()];
};

const formatAuditPlanDateList = (formData = {}) => {
  const values = Array.from(new Set((formData?.rundowns || [])
    .map((row) => dateToIso(row?.date))
    .filter(Boolean)))
    .sort();

  if (values.length > 0) {
    return values.map(formatAuditPlanDate).join(', ');
  }

  if (formData?.auditStartDate && formData?.auditEndDate) {
    if (dateToIso(formData.auditStartDate) === dateToIso(formData.auditEndDate)) {
      return formatAuditPlanDate(formData.auditStartDate);
    }
    return `${formatAuditPlanDate(formData.auditStartDate)}, ${formatAuditPlanDate(formData.auditEndDate)}`;
  }

  return formatAuditPlanDate(formData?.auditStartDate || formData?.auditEndDate || '');
};

const formatMandayText = (value) => {
  const parsed = Number(value);
  if (Number.isFinite(parsed) && parsed > 0) return `${parsed} Man-day`;
  return d(value);
};

const stringifyMultiValue = (value) => Array.isArray(value) ? value.filter(Boolean).join(', ') : String(value || '');

const getUniqueIsoDates = (values = []) => Array.from(new Set(values.map(dateToIso).filter(Boolean))).sort();

const resolveAttendanceDates = (f = {}, projectData = {}) => {
  const explicit = getUniqueIsoDates([
    ...(Array.isArray(f.attendanceDates) ? f.attendanceDates : []),
    ...(Array.isArray(f.dates) ? f.dates : []),
    f.date,
  ]);
  if (explicit.length > 0) return explicit;

  const scheduleDates = getUniqueIsoDates((projectData?.auditSchedule?.auditSchedules || [])
    .flatMap((schedule) => [
      ...(Array.isArray(schedule?.mandayDetails) ? schedule.mandayDetails.map((row) => row?.date) : []),
      ...(Array.isArray(schedule?.siteLocationDetails) ? schedule.siteLocationDetails.map((row) => row?.date) : []),
      schedule?.startDate,
      schedule?.endDate,
    ]));
  if (scheduleDates.length > 0) return scheduleDates;

  return getUniqueIsoDates([projectData?.auditSchedule?.startDate, projectData?.auditSchedule?.endDate, projectData?.estimatedAuditDate]);
};

const resolveCertificateLocations = (f = {}, projectData = {}) => {
  const source = Array.isArray(f.additionalLocations) && f.additionalLocations.length > 0
    ? f.additionalLocations
    : [
        ...(Array.isArray(projectData?.auditSchedule?.siteLocations) ? projectData.auditSchedule.siteLocations : []),
        ...(Array.isArray(projectData?.sites) ? projectData.sites : []),
        ...(Array.isArray(projectData?.projectSites) ? projectData.projectSites : []),
      ];
  return source
    .map((loc, i) => ({
      siteFunction: loc?.siteFunction || loc?.siteName || loc?.name || loc?.label || `Site ${i + 1}`,
      scope: loc?.scope || loc?.scopeOfCertification || projectData?.scopeLabel || projectData?.scope || '',
      address: loc?.address || loc?.siteAddress || loc?.location || '',
    }))
    .filter((loc) => String(loc.siteFunction || loc.scope || loc.address || '').trim());
};

const isInitialStageAudit = (auditType = '') => /initial/i.test(String(auditType || ''));

const getAuditorMap = (auditors = []) => new Map(
  (Array.isArray(auditors) ? auditors : [])
    .map((auditor) => {
      const id = String(auditor?.userId || auditor?.id || auditor?.employeeId || '').trim();
      const name = String(auditor?.name || auditor?.fullName || auditor?.label || auditor?.employeeName || '').trim();
      return id ? [id, name] : null;
    })
    .filter(Boolean)
);

const resolveAuditPlanAuditors = (row, auditorMap) => {
  const names = (Array.isArray(row?.auditorIds) ? row.auditorIds : [])
    .map((id) => auditorMap.get(String(id)))
    .filter(Boolean);
  return names.length > 0 ? names.join(', ') : d(row?.auditorNames || '');
};

const getGroupedAuditPlanRows = (rundowns = []) => {
  const map = new Map();
  (Array.isArray(rundowns) ? rundowns : []).forEach((row) => {
    const key = dateToIso(row?.date) || 'undated';
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(row);
  });
  return Array.from(map.entries())
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([date, rows], index) => ({ date, rows, index: index + 1 }));
};

const drawBrand = (doc, branding = {}, x = 14, y = 6, w = 26, h = 16) => {
  if (branding.logo) {
    try {
      doc.addImage(branding.logo, 'PNG', x, y, w, h);
      return;
    } catch (_) {
      // Ignore invalid image payload
    }
  }
  doc.setTextColor(0, 0, 0);
  doc.setFont(undefined, 'bold');
  doc.setFontSize(14);
  doc.text('CBQA', x, y + 8);
  doc.setFont(undefined, 'normal');
  doc.setFontSize(7);
  doc.text('WE', x + 20, y + 6);
  doc.text('INSPIRE', x + 20, y + 9);
  doc.text('IN TRUST', x + 20, y + 12);
};

const addAuditQPageChrome = (doc, {
  title,
  footerCode,
  branding = {},
  showPageCount = true,
  releaseDate = '',
  documentNumber = '',
} = {}) => {
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const pageCount = doc.getNumberOfPages();

  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    drawBrand(doc, branding);
    doc.setFillColor(...COLOR_SECTION);
    doc.rect(14, 24, W - 28, 5.5, 'F');
    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, 'bold');
    doc.setFontSize(10);
    doc.text(title || '', 16, 28);
    if (showPageCount) {
      doc.setFont(undefined, 'normal');
      doc.text(`Page ${page} of ${pageCount}`, W - 14, 34.3, { align: 'right' });
    }
    if (documentNumber || releaseDate) {
      doc.setFontSize(7);
      if (documentNumber) doc.text(`Document #: ${documentNumber}`, 14, 34.3);
      if (releaseDate) doc.text(`Release Date: ${releaseDate}`, W / 2, 34.3, { align: 'center' });
    }
    doc.setDrawColor(0, 0, 0);
    doc.line(14, 35.2, W - 14, 35.2);

    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, 'normal');
    doc.setFontSize(8);
    if (footerCode) doc.text(footerCode, 14, H - 8);
    doc.text('Un-controlled document if printed', W / 2, H - 8, { align: 'center' });
  }
};

const addSummaryChrome = (doc, f = {}, projectData = {}, branding = {}) => {
  const pageCount = doc.getNumberOfPages();
  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();
    if (page === 1) {
      drawBrand(doc, branding, W - 64, 8, 30, 20);
      continue;
    }
    drawBrand(doc, branding);
    doc.setFillColor(...COLOR_SECTION);
    doc.rect(14, 24, W - 28, 5.5, 'F');
    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, 'bold');
    doc.setFontSize(10);
    doc.text(FORM_TITLES.SUMMARY, 16, 28);
    doc.setFont(undefined, 'normal');
    doc.text(`Page ${page} of ${pageCount}`, W - 14, 34.3, { align: 'right' });
    doc.setDrawColor(0, 0, 0);
    doc.line(14, 35.2, W - 14, 35.2);
    doc.setFontSize(8);
    doc.text(d(getClientName(f, projectData)), 14, H - 8);
  }
};

const drawAuditPlanLine = (doc, label, value, y, options = {}) => {
  const W = doc.internal.pageSize.getWidth();
  const labelX = options.labelX ?? 18;
  const valueX = options.valueX ?? 52;
  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  doc.text(label, labelX, y);
  doc.setFont(undefined, 'bold');
  doc.text(String(value || ''), valueX, y);
  if (options.rightText) {
    doc.setFont(undefined, 'normal');
    doc.text(String(options.rightText), W - 14, y, { align: 'right' });
  }
  doc.setDrawColor(150, 150, 150);
  doc.line(14, y + 4, W - 14, y + 4);
  return y + (options.spacing || 12);
};

const drawAuditPlanWrappedLine = (doc, label, value, y, options = {}) => {
  const W = doc.internal.pageSize.getWidth();
  const labelX = options.labelX ?? 18;
  const valueX = options.valueX ?? 52;
  const maxWidth = options.maxWidth ?? (W - valueX - 16);
  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  doc.text(label, labelX, y);
  doc.setFont(undefined, 'bold');
  const lines = doc.splitTextToSize(String(value || ''), maxWidth);
  doc.text(lines, valueX, y);
  const bottomY = y + Math.max(lines.length, 1) * 4 + 2;
  doc.setDrawColor(150, 150, 150);
  doc.line(14, bottomY, W - 14, bottomY);
  return bottomY + 8;
};

const normalizeDocCode = (rawCode = '') => {
  if (!rawCode) return '';
  const prefixes = ['FAPP-01','FAPP-02','FAPP-03','FAPP-04','FAPP-05','FAPP-06','FAPP-07','FAPP-08','FTRD-01','FTRD-02'];
  for (const p of prefixes) {
    if (rawCode.startsWith(p)) return p;
  }
  if (rawCode === 'Audit Summary Report') return 'SUMMARY';
  return rawCode;
};

const sectionTitle = (doc, text, y) => {
  const W = doc.internal.pageSize.getWidth();
  doc.setFillColor(...COLOR_SECTION);
  doc.rect(14, y - 1, W - 28, 5.2, 'F');
  doc.setFontSize(9);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(0, 0, 0);
  doc.text(text, 16, y + 2.7);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(0, 0, 0);
  return y + 6;
};

const addTopMetaTable = (doc, f = {}, projectData = {}, startY = FORM_TOP_Y, options = {}) => {
  const body = options.compact
    ? [[
        { content: `Client Name: ${d(getClientName(f, projectData))}`, styles: { fontStyle: 'bold' } },
        `Client ID: ${d(getClientId(f, projectData))}`,
      ], [
        `Audit Criteria: ${d(getAuditCriteria(f, projectData))}`,
        `Activity: ${d(getAuditActivity(f, projectData))}`,
      ], [
        `Lead Auditor Name: ${d(getLeadAuditorName(f, projectData))}`,
        `Date of Activity: ${d(getAuditDates(f, projectData))}`,
      ]]
    : [[
        { content: `Client Name: ${d(getClientName(f, projectData))}`, styles: { fontStyle: 'bold' } },
        `Client ID#: ${d(getClientId(f, projectData))}`,
      ], [
        `Activity: ${d(getAuditActivity(f, projectData))}`,
        `Date(s) of audit: ${d(getAuditDates(f, projectData))}`,
      ]];

  autoTable(doc, {
    ...TABLE_GRID_STYLES,
    startY,
    margin: { left: 14, right: 14 },
    body,
    columnStyles: { 0: { cellWidth: options.leftWidth || 95 } },
  });
  return (doc.lastAutoTable?.finalY ?? startY) + 4;
};

const addMultiAuditCriteriaNote = (doc, y) => {
  doc.setFontSize(7);
  doc.setTextColor(0, 0, 0);
  doc.text('Multiple Audit Criteria:', 14, y);
  doc.rect(50, y - 3, 2.5, 2.5);
  doc.text('Note: If the activity covers more than one audit criteria, one copy of this form is to be prepared for each audit criteria and copies of', 14, y + 4);
  doc.text('the audit report package are to be distributed accordingly.', 14, y + 7.5);
  return y + 11;
};

// ── Renders ────────────────────────────────────────────────────────────────

const renderFAPP01 = async (doc, f, projectData = {}) => {
  const [logoResult, signatureResult] = await Promise.allSettled([
    loadImageAsDataUrl('/static/logo.png'),
    loadImageAsDataUrl('/static/signature.png'),
  ]);
  const branding = {
    logo: logoResult.status === 'fulfilled' ? logoResult.value : null,
    signature: signatureResult.status === 'fulfilled' ? signatureResult.value : null,
  };
  const W = doc.internal.pageSize.getWidth();
  const auditorMap = getAuditorMap(projectData?.auditors);
  let y = 42;

  y = drawAuditPlanLine(
    doc,
    'Issue date:',
    formatIsoLikeDate(f.issueDate || new Date().toISOString().slice(0, 10)),
    y,
    {
      labelX: 18,
      valueX: 38,
      rightText: `${f.revDate ? `Rev. Date: ${formatIsoLikeDate(f.revDate)}` : 'Rev. Date:'}${f.executed ? '      (x) as executed' : ''}`,
      spacing: 14,
    }
  );
  y = drawAuditPlanLine(doc, 'Client ID:', projectData?.clientId || '', y, {
    labelX: 18,
    valueX: 36,
    rightText: `Company: ${f.clientCompany || projectData?.companyName || projectData?.clientName || ''}`,
    spacing: 14,
  });
  y = drawAuditPlanLine(doc, 'PIC / Position:', f.picPosition || '', y);
  y = drawAuditPlanWrappedLine(doc, 'Audit Site (s):', f.auditSite || projectData?.address || projectData?.locationName || '', y);
  y = drawAuditPlanLine(doc, 'Audit Date (s):', formatAuditPlanDateList(f), y);

  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  doc.text('On-site Audit Days:', 18, y);
  doc.setFont(undefined, 'bold');
  doc.text(formatMandayText(f.onSiteAuditDays), 56, y);
  doc.setFont(undefined, 'normal');
  doc.text('Off-site Audit Days:', 92, y);
  doc.setFont(undefined, 'bold');
  doc.text(formatMandayText(f.offSiteAuditDays), 132, y);
  doc.line(14, y + 4, W - 14, y + 4);
  y += 12;

  doc.setFont(undefined, 'bold');
  doc.setFontSize(10);
  doc.text('Audit Criteria', 18, y);
  y += 5;
  doc.setFont(undefined, 'normal');
  doc.text(`Standard(s). Ref. : ${stringifyMultiValue(f.applicableStandards) || d(projectData?.auditCriteria)}`, 18, y);
  y += 4.5;
  doc.setFont(undefined, 'italic');
  const criteriaLines = doc.splitTextToSize(String(f.auditCriteriaDetails || 'Processes and documentation of the management system developed by the company'), W - 36);
  doc.text(criteriaLines, 18, y);
  y += Math.max(criteriaLines.length, 1) * 4 + 2;
  doc.line(14, y, W - 14, y);
  y += 10;

  y = drawAuditPlanLine(doc, 'Audit type', f.auditType || '', y, { valueX: 58 });
  y = drawAuditPlanWrappedLine(doc, 'Certification Scope :', f.scopeOfCertification || projectData?.scopeLabel || projectData?.scope || '', y, { valueX: 58 });
  y = drawAuditPlanLine(doc, 'NACE/Product/Service/EAC Code(s):', f.naceCode || projectData?.eaCode || 'No-code', y, { valueX: 88 });

  doc.setFont(undefined, 'normal');
  doc.text('No of Shift:', 18, y);
  doc.setFont(undefined, 'bold');
  doc.text(d(f.noOfShift), 40, y);
  doc.setFont(undefined, 'normal');
  doc.text('Shifts # to be audited', 92, y);
  doc.setFont(undefined, 'bold');
  doc.text(d(f.shiftToBeAudited), 142, y);
  doc.setFont(undefined, 'normal');
  doc.text('(Ref to audit programme):', 92, y + 4);
  doc.line(14, y + 8, W - 14, y + 8);
  y += 16;

  y = drawAuditPlanWrappedLine(doc, 'Audit Team / Role :', f.auditTeamRole || '', y, { valueX: 54 });
  y = drawAuditPlanLine(doc, 'Audit Language :', f.auditLanguage || 'Bahasa Indonesia', y, { valueX: 52 });
  y = drawAuditPlanLine(doc, 'Prepared by :', f.preparedBy || '', y, { valueX: 44 });

  doc.addPage();
  let objectiveY = 42;
  doc.setFontSize(10);
  doc.setFont(undefined, 'bold');
  doc.text('Audit Objectives:', 18, objectiveY);
  objectiveY += 6;
  doc.setFont(undefined, 'normal');
  doc.rect(18, objectiveY - 3.5, 3.2, 3.2);
  doc.text('x', 18.9, objectiveY - 0.7);
  const objectiveLines = doc.splitTextToSize(String(f.auditObjectives || ''), W - 32);
  doc.text(objectiveLines, 24, objectiveY);
  objectiveY += Math.max(objectiveLines.length, 1) * 4 + 4;

  const tableBody = [];
  getGroupedAuditPlanRows(f.rundowns).forEach((group) => {
    const dateLabel = group.date === 'undated' ? '' : `${formatAuditPlanDate(group.date)} – ${formatWeekday(group.date)}`;
    tableBody.push([
      {
        content: `Day ${group.index}${dateLabel ? ` - ${dateLabel}` : ''}`,
        colSpan: 5,
        styles: { halign: 'center', fontStyle: 'bold', fillColor: [245, 245, 245] },
      },
    ]);

    group.rows.forEach((row) => {
      const timeRange = `${row?.timeFrom || '-'}${row?.timeTo ? ` - ${row.timeTo}` : ''}`;
      if (String(row?.type || '').toLowerCase() === 'break') {
        tableBody.push([
          {
            content: `${timeRange} ${row?.clientProcesses || 'BREAK / LUNCH'}`.trim(),
            colSpan: 5,
            styles: { halign: 'center', fontStyle: 'bold' },
          },
        ]);
        return;
      }

      tableBody.push([
        timeRange,
        resolveAuditPlanAuditors(row, auditorMap),
        d(row?.clientProcesses),
        [d(row?.processOwner), stringifyMultiValue(row?.clausulAnnex)].filter(Boolean).join('\n'),
        d(row?.locationSite),
      ]);
    });
  });

  autoTable(doc, {
    startY: objectiveY,
    margin: { left: 14, right: 14, bottom: 20 },
    theme: 'grid',
    styles: { fontSize: 8.2, cellPadding: 1.6, lineColor: [0, 0, 0], lineWidth: 0.1, valign: 'top' },
    head: [['TIME', 'AUDITOR', 'CLIENT PROCESSES', 'PROCESS OWNER', 'LOCATION/SITE']],
    body: tableBody.length > 0 ? tableBody : [[{ content: 'No audit plan schedule available', colSpan: 5, styles: { halign: 'center' } }]],
    headStyles: { fillColor: [255, 255, 255], textColor: 0, lineColor: [0, 0, 0], lineWidth: 0.2, fontStyle: 'bold' },
    columnStyles: {
      0: { cellWidth: 26 },
      1: { cellWidth: 38 },
      2: { cellWidth: 55 },
      3: { cellWidth: 40 },
      4: { cellWidth: 26 },
    },
  });

  doc.addPage();
  let notesY = 42;
  doc.setFont(undefined, 'normal');
  doc.setFontSize(10);
  const introLines = doc.splitTextToSize('To better prepare us for the audit and to better understand how we can tailor our activities to add most value to your organization, please help us by providing the following information, if applicable or available:', W - 28);
  doc.text(introLines, 14, notesY);
  notesY += introLines.length * 4 + 5;

  splitLines(f.preAuditRequests, AUDIT_PLAN_DEFAULT_REQUESTS).forEach((item) => {
    const bulletLines = doc.splitTextToSize(item, W - 40);
    doc.text('\u2022', 22, notesY);
    doc.text(bulletLines, 28, notesY);
    notesY += bulletLines.length * 4 + 2;
  });

  notesY += 10;
  doc.setFont(undefined, 'bold');
  doc.text('ADDITIONAL INFORMATION:', 14, notesY);
  notesY += 7;
  doc.setFont(undefined, 'normal');
  splitLines(f.additionalInformation, AUDIT_PLAN_DEFAULT_NOTES).forEach((item, index) => {
    const lines = doc.splitTextToSize(item, W - 34);
    doc.text(`${index + 1}.`, 22, notesY);
    doc.text(lines, 30, notesY);
    notesY += lines.length * 4 + 2;
  });

  if (branding.signature) {
    try {
      doc.addImage(branding.signature, 'PNG', 14, 236, 28, 12);
    } catch (_) {
      // Ignore invalid image payload
    }
  }

  const totalPages = doc.getNumberOfPages();
  addAuditQPageChrome(doc, {
    title: FORM_TITLES['FAPP-01'],
    footerCode: AUDIT_PLAN_FOOTER_CODE,
    branding,
    showPageCount: totalPages > 1,
  });
};

const renderFAPP02 = (doc, f, startY, projectData = {}) => {
  let y = addTopMetaTable(doc, f, projectData, startY, { leftWidth: 84 });
  // ── Meta info row (reference, prepared by, approved by) ──────────────────
  autoTable(doc, {
    ...TABLE_GRID_STYLES,
    startY: y,
    margin: { left: 14, right: 14 },
    body: [
      [
        { content: 'Standard(s)/Audit Criteria:', styles: { fontStyle: 'bold' } },
        d(getAuditCriteria(f, projectData)),
        { content: 'Audit Type:', styles: { fontStyle: 'bold' } },
        d(getAuditActivity(f, projectData)),
      ],
      [
        { content: 'Scope of Certification:', styles: { fontStyle: 'bold' } },
        { content: d(f.scopeOfCertification || projectData?.scopeLabel || projectData?.scope), colSpan: 3 },
      ],
      [
        { content: 'Surveillance Interval:', styles: { fontStyle: 'bold' } },
        `${d(f.surveillanceInterval || '12')} months`,
        { content: 'PII Role:', styles: { fontStyle: 'bold' } },
        [f.piiController ? 'Controller' : '', f.piiProcessor ? 'Processor' : ''].filter(Boolean).join(' / ') || '-',
      ],
    ],
    columnStyles: {
      0: { cellWidth: 45 },
      1: { cellWidth: 65 },
      2: { cellWidth: 26 },
    },
  });

  // ── Three-year schedule grid ──────────────────────────────────────────────
  const scheduleY = sectionTitle(doc, 'Mandatory Requirements / Processes (Audit notes to support coverage)', (doc.lastAutoTable?.finalY ?? y) + 5);
  const years = [
    ['year1', 'Year 1 (Initial)'],
    ['year2', 'Year 2 (Surveillance 1)'],
    ['year3', 'Year 3 (Surveillance 2)'],
  ];
  const legacyRows = years.map(([key, label]) => {
    const yr = f[key] || {};
    return [d(yr.clauseAnnex || ''), d(yr.scope || label), d(yr.mandays || ''), '', '', fmtDate(yr.tentativeDateFrom), fmtDate(yr.tentativeDateTo), '', '', '', d(yr.auditType)];
  });
  const programRows = Array.isArray(f.programRows) && f.programRows.length > 0
    ? f.programRows.map((row) => [d(row.clauseAnnex), d(row.name), d(row.rc), d(row.st1), d(row.st2), d(row.sv1), d(row.sv2), d(row.sv3), d(row.sv4), d(row.sv5), d(row.rc2)])
    : legacyRows;
  autoTable(doc, {
    ...TABLE_GRID_STYLES,
    startY: scheduleY,
    margin: { left: 14, right: 14 },
    head: [['Clause/Annex', 'Process / Requirement', 'RC', 'ST1', 'ST2', 'SV1', 'SV2', 'SV3', 'SV4', 'SV5', 'RC2']],
    body: programRows,
    styles: { ...TABLE_GRID_STYLES.styles, fontSize: 6.8, cellPadding: 1.2 },
    columnStyles: {
      0: { cellWidth: 26 },
      2: { halign: 'center' }, 3: { halign: 'center' }, 4: { halign: 'center' },
      5: { halign: 'center' }, 6: { halign: 'center' }, 7: { halign: 'center' },
      8: { halign: 'center' }, 9: { halign: 'center' }, 10: { halign: 'center' },
    },
  });

  if (Array.isArray(f.siteSamplingRows) && f.siteSamplingRows.length > 0) {
    autoTable(doc, {
      ...TABLE_GRID_STYLES,
      startY: sectionTitle(doc, 'Sites Sampling Program', (doc.lastAutoTable?.finalY ?? scheduleY) + 5),
      margin: { left: 14, right: 14 },
      head: [['Site', 'Branch Hierarchy', 'Type', 'IA', 'SV1', 'SV2', 'RC', 'Supp.', 'Ext.']],
      body: f.siteSamplingRows.map((row) => [d(row.site), d(row.branchHierarchy), d(row.type), d(row.ia), d(row.sv1), d(row.sv2), d(row.rc), d(row.supplementary), d(row.extension)]),
      styles: { ...TABLE_GRID_STYLES.styles, fontSize: 7.2, cellPadding: 1.4 },
    });
  }

  // ── Notes (only if present) ───────────────────────────────────────────────
  if (String(f.notes || '').trim()) {
    autoTable(doc, {
      startY: (doc.lastAutoTable?.finalY ?? startY) + 5,
      margin: { left: 14, right: 14 },
      theme: 'grid',
      styles: { fontSize: 8.5, cellPadding: 2 },
      body: [
        [
          { content: 'Notes', styles: { fontStyle: 'bold', fillColor: [240, 248, 248], cellWidth: 20 } },
          [d(f.notes), `Prepared by: ${d(f.preparedBy)}`, `Amended by: ${d(f.amendedBy)}`, `Approved by: ${d(f.approvedBy)}`].join('\n'),
        ],
      ],
    });
  }
};

const renderFAPP03 = (doc, f, startY, projectData = {}) => {
  const dates = resolveAttendanceDates(f, projectData);
  const pages = dates.length > 0 ? dates : [''];

  pages.forEach((date, pageIndex) => {
    if (pageIndex > 0) doc.addPage();
    const y = addTopMetaTable(doc, { ...f, date }, projectData, startY);
    autoTable(doc, {
      ...TABLE_GRID_STYLES,
      startY: y,
      margin: { left: 14, right: 14 },
      head: [['Field', 'Value']],
      body: [
        ['Date', fmtDate(date || f.date)],
        ['Audit Type', d(f.auditType)],
        ['Meeting Type', d(f.meetingType)],
        ['Location', d(f.location)],
      ],
      columnStyles: { 0: { cellWidth: 55, fontStyle: 'bold' } },
    });

    autoTable(doc, {
      ...TABLE_GRID_STYLES,
      startY: doc.lastAutoTable.finalY + 6,
      margin: { left: 14, right: 14 },
      head: [['#', 'Name', 'Position', 'Email', 'Phone', 'Opening Signature', 'Closing Signature']],
      body: (f.attendees || []).slice(0, 15).map((a, i) => [
        i + 1, d(a.name), d(a.position), d(a.email), d(a.phoneNumber),
        d(a.openingSignature), d(a.closingSignature),
      ]),
      styles: { ...TABLE_GRID_STYLES.styles, fontSize: 7, cellPadding: 1.4 },
      columnStyles: {
        0: { cellWidth: 7 }, 1: { cellWidth: 28 }, 2: { cellWidth: 25 },
        3: { cellWidth: 34 }, 4: { cellWidth: 24 }, 5: { cellWidth: 30 }, 6: { cellWidth: 30 },
      },
    });
  });
};

const renderFAPP04 = (doc, f, startY, projectData = {}) => {
  const y = addTopMetaTable(doc, f, projectData, startY, { compact: true, leftWidth: 88 });
  autoTable(doc, {
    ...TABLE_GRID_STYLES,
    startY: y,
    margin: { left: 14, right: 14 },
    head: [['Field', 'Value']],
    body: [
      ['Audit Date', fmtDate(f.auditDate)],
      ['Auditor', d(f.auditorName)],
      ['Process / Area', d(f.processArea)],
    ],
    columnStyles: { 0: { cellWidth: 55, fontStyle: 'bold' } },
  });

  const notesText = String(f.notesText || '').trim();
  if (notesText) {
    autoTable(doc, {
      ...TABLE_GRID_STYLES,
      startY: doc.lastAutoTable.finalY + 6,
      margin: { left: 14, right: 14 },
      head: [['Audit Notes']],
      body: [[notesText]],
    });
  } else {
    autoTable(doc, {
      ...TABLE_GRID_STYLES,
      startY: doc.lastAutoTable.finalY + 6,
      margin: { left: 14, right: 14 },
      head: [['Clause', 'Conformity', 'Observation', 'Evidence']],
      body: (f.notes || []).map((n) => [d(n.clause), d(n.conformity), d(n.observation), d(n.evidence)]),
      columnStyles: { 0: { cellWidth: 18 }, 1: { cellWidth: 28 } },
    });
  }
};

const renderFAPP05 = (doc, f, startY, projectData = {}) => {
  let tableEndY = addTopMetaTable(doc, f, projectData, startY, { compact: true, leftWidth: 88 });
  const scheduleDates = resolveAttendanceDates({}, projectData);
  const auditDateFrom = f.auditDateFrom || scheduleDates[0] || '';
  const auditDateTo = f.auditDateTo || scheduleDates[scheduleDates.length - 1] || auditDateFrom;
  const location = firstValue(f.location, (projectData?.auditSchedule?.siteLocations || []).map((site) => site?.siteName || site?.address).filter(Boolean).join(', '), projectData.locationName);

  // ── Part 1: General Audit Data ────────────────────────────────────────────
  const p1Y = sectionTitle(doc, 'PART 1 — GENERAL AUDIT DATA', tableEndY);
  autoTable(doc, {
    ...TABLE_GRID_STYLES,
    startY: p1Y,
    margin: { left: 14, right: 14 },
    body: [
      [
        { content: 'Report Number', styles: { fontStyle: 'bold' } },
        d(f.reportNumber),
        { content: 'Audit Type', styles: { fontStyle: 'bold' } },
        d(f.auditType),
      ],
      [
        { content: 'Audit Date From', styles: { fontStyle: 'bold' } },
        fmtDate(auditDateFrom),
        { content: 'Audit Date To', styles: { fontStyle: 'bold' } },
        fmtDate(auditDateTo),
      ],
      [
        { content: 'Applicable Standards', styles: { fontStyle: 'bold' } },
        { content: d(f.applicableStandards), colSpan: 3 },
      ],
      [
        { content: 'Scope of Certification', styles: { fontStyle: 'bold' } },
        { content: d(f.scopeOfCertification), colSpan: 3 },
      ],
      [
        { content: 'Location', styles: { fontStyle: 'bold' } },
        d(location),
        { content: 'Lead Auditor', styles: { fontStyle: 'bold' } },
        d(f.leadAuditor),
      ],
    ],
    columnStyles: {
      0: { cellWidth: 45 },
      1: { cellWidth: 55 },
      2: { cellWidth: 38 },
      // col 3: fills remaining width
    },
  });
  tableEndY = doc.lastAutoTable?.finalY ?? tableEndY;

  // ── Part 2: Lead Auditor Recommendation ───────────────────────────────────
  const recText = LEAD_AUDITOR_RECOMMENDATION_MAP[f.leadAuditorRecommendation]
    || (f.leadAuditorRecommendation ? d(f.leadAuditorRecommendation) : '-');
  const p2Y = sectionTitle(doc, 'PART 2 — LEAD AUDITOR RECOMMENDATION', tableEndY + 6);
  autoTable(doc, {
    startY: p2Y,
    margin: { left: 14, right: 14 },
    theme: 'grid',
    styles: { fontSize: 8.5, cellPadding: 3 },
    body: [[recText]],
  });
  tableEndY = doc.lastAutoTable?.finalY ?? tableEndY;

  // ── Part 3: Executive Summary ─────────────────────────────────────────────
  if (String(f.executiveSummary || '').trim()) {
    const p3Y = sectionTitle(doc, 'PART 3 — EXECUTIVE SUMMARY', tableEndY + 6);
    autoTable(doc, {
      startY: p3Y,
      margin: { left: 14, right: 14 },
      theme: 'grid',
      styles: { fontSize: 8.5, cellPadding: 3 },
      body: [[d(f.executiveSummary)]],
    });
    tableEndY = doc.lastAutoTable?.finalY ?? tableEndY;
  }

  // ── Part 4: Strengths & Weaknesses ────────────────────────────────────────
  if (String(f.strengths || '').trim() || String(f.weakness || '').trim()) {
    const p4Y = sectionTitle(doc, 'PART 4 — STRENGTHS & WEAKNESSES', tableEndY + 6);
    autoTable(doc, {
      startY: p4Y,
      margin: { left: 14, right: 14 },
      theme: 'grid',
      styles: { fontSize: 8.5, cellPadding: 3 },
      head: [['STRENGTHS', 'WEAKNESSES / AREAS FOR IMPROVEMENT']],
      body: [[d(f.strengths), d(f.weakness)]],
      headStyles: { fillColor: COLOR_TEAL, textColor: 255, fontStyle: 'bold' },
    });
    tableEndY = doc.lastAutoTable?.finalY ?? tableEndY;
  }

  // ── Part 5: Findings Summary ──────────────────────────────────────────────
  const fs = f.findingsSummary || {};
  const previous = f.previousActivity || {};
  const initialHeaders = isInitialStageAudit(f.auditType);
  const issuedHeader = initialHeaders ? 'AoC\nISSUES' : 'MAJOR\nISSUED';
  const closedHeader = initialHeaders ? 'AoC\nCLOSED' : 'MAJOR\nCLOSED';
  const p5Y = sectionTitle(doc, 'PART 5 — FINDINGS SUMMARY', tableEndY + 6);
  const findingsRows = [
    ['Current Activity', d(fs.majorIssued ?? 0), d(fs.majorClosed ?? 0), d(fs.minorIssued ?? 0), d(fs.minorClosed ?? 0), d(fs.openNCs ?? 0)],
  ];
  if (f.showPreviousActivityInExport !== false) {
    findingsRows.unshift(['Previous Activity', d(previous.majorIssued ?? 0), d(previous.majorClosed ?? 0), d(previous.minorIssued ?? 0), d(previous.minorClosed ?? 0), d(previous.openNCs ?? 0)]);
  }
  autoTable(doc, {
    startY: p5Y,
    margin: { left: 14, right: 14 },
    theme: 'grid',
    styles: { fontSize: 8.5, cellPadding: 2 },
    head: [['CATEGORY', issuedHeader, closedHeader, 'MINOR\nISSUED', 'MINOR\nCLOSED', '# OPEN NCs']],
    body: findingsRows,
    headStyles: { fillColor: COLOR_TEAL, textColor: 255, fontStyle: 'bold', fontSize: 8 },
    columnStyles: {
      0: { cellWidth: 50, fontStyle: 'bold' },
      1: { halign: 'center' },
      2: { halign: 'center' },
      3: { halign: 'center' },
      4: { halign: 'center' },
      5: { halign: 'center' },
    },
  });
  tableEndY = doc.lastAutoTable?.finalY ?? tableEndY;

  if (String(f.statusOfPreviousFindings || '').trim()) {
    autoTable(doc, {
      startY: tableEndY + 3,
      margin: { left: 14, right: 14 },
      theme: 'grid',
      styles: { fontSize: 8.5, cellPadding: 2 },
      head: [['STATUS OF PREVIOUS FINDINGS']],
      body: [[d(f.statusOfPreviousFindings)]],
      headStyles: { fillColor: COLOR_TEAL, textColor: 255, fontStyle: 'bold' },
    });
    tableEndY = doc.lastAutoTable?.finalY ?? tableEndY;
  }

  if (String(f.auditResult || '').trim() || String(f.notesToNonConformity || '').trim()) {
    autoTable(doc, {
      ...TABLE_GRID_STYLES,
      startY: tableEndY + 3,
      margin: { left: 14, right: 14 },
      body: [
        [{ content: 'AUDIT RESULT', styles: { fontStyle: 'bold' } }, d(f.auditResult)],
        [{ content: 'NOTES TO NONCONFORMITY', styles: { fontStyle: 'bold' } }, d(f.notesToNonConformity)],
      ],
      columnStyles: { 0: { cellWidth: 55 } },
    });
    tableEndY = doc.lastAutoTable?.finalY ?? tableEndY;
  }

  if (String(f.notes || '').trim()) {
    autoTable(doc, {
      startY: tableEndY + 3,
      margin: { left: 14, right: 14 },
      theme: 'grid',
      styles: { fontSize: 8.5, cellPadding: 2 },
      head: [['NOTES']],
      body: [[d(f.notes)]],
      headStyles: { fillColor: COLOR_TEAL, textColor: 255, fontStyle: 'bold' },
    });
    tableEndY = doc.lastAutoTable?.finalY ?? tableEndY;
  }

  // ── Part 6: Audit Summary ─────────────────────────────────────────────────
  const part6Rows = [
    ['Audit Summary', f.auditSummary],
    ['Compliance to ISMS', f.complianceToISMS],
    ['Verification of Scope', f.verificationOfScope],
    ['Verification of Statement (VOS)', f.verificationOfStatement],
    ['Policy and Commitment', f.policyCommitment],
    ['Conclusion', f.conclusion],
    ['Risk Management', f.riskManagement],
    ['Statement of Applicability (SOA)', f.statementOfApplicability],
    ['Objectives', f.objectives],
  ].filter(([, value]) => String(value || '').trim());
  if (part6Rows.length > 0) {
    const p6Y = sectionTitle(doc, 'PART 6 — AUDIT SUMMARY', tableEndY + 6);
    autoTable(doc, {
      startY: p6Y,
      margin: { left: 14, right: 14 },
      theme: 'grid',
      styles: { fontSize: 8.5, cellPadding: 3 },
      body: part6Rows.map(([label, value]) => [
        { content: label, styles: { fontStyle: 'bold', fillColor: [240, 248, 248] } },
        d(value),
      ]),
      columnStyles: { 0: { cellWidth: 58 } },
    });
    tableEndY = doc.lastAutoTable?.finalY ?? tableEndY;
  }

  // ── Part 7: Acknowledgment ────────────────────────────────────────────────
  if (f.clientRepName || f.clientEmail || f.clientAddressContact || f.reportAcknowledgedByDifferent) {
    const p7Y = sectionTitle(doc, 'PART 7 — CLIENT ACKNOWLEDGEMENT', tableEndY + 6);
    autoTable(doc, {
      startY: p7Y,
      margin: { left: 14, right: 14 },
      theme: 'grid',
      styles: { fontSize: 8.5, cellPadding: 2.5 },
      body: [
        [{ content: "Client's Representative Name", styles: { fontStyle: 'bold', fillColor: [240, 248, 248] } }, d(f.clientRepName)],
        [{ content: 'Position / Title', styles: { fontStyle: 'bold', fillColor: [240, 248, 248] } }, d(f.clientRepPosition)],
        [{ content: 'Report acknowledged by (if different)', styles: { fontStyle: 'bold', fillColor: [240, 248, 248] } }, d(f.reportAcknowledgedByDifferent)],
        [{ content: 'Email', styles: { fontStyle: 'bold', fillColor: [240, 248, 248] } }, d(f.clientEmail)],
        [{ content: 'Phone / Mobile', styles: { fontStyle: 'bold', fillColor: [240, 248, 248] } }, d(f.clientPhone)],
        [{ content: 'Address & Contact Detail', styles: { fontStyle: 'bold', fillColor: [240, 248, 248] } }, d(f.clientAddressContact)],
      ],
      columnStyles: { 0: { cellWidth: 60 } },
    });
    tableEndY = doc.lastAutoTable?.finalY ?? tableEndY;
  }

  if (String(f.part8FollowUp || '').trim() || String(f.part8Distribution || '').trim()) {
    const p8Y = sectionTitle(doc, 'PART 8 — FOLLOW-UP AND DISTRIBUTION', tableEndY + 6);
    autoTable(doc, {
      ...TABLE_GRID_STYLES,
      startY: p8Y,
      margin: { left: 14, right: 14 },
      body: [
        [{ content: 'Follow-up / Additional Information', styles: { fontStyle: 'bold' } }, d(f.part8FollowUp)],
        [{ content: 'Report Distribution / Remarks', styles: { fontStyle: 'bold' } }, d(f.part8Distribution)],
      ],
      columnStyles: { 0: { cellWidth: 62 } },
    });
  }
};

const renderFAPP06 = (doc, f, startY, projectData = {}) => {
  let y = addTopMetaTable(doc, f, projectData, startY);
  // ── Client Contact ────────────────────────────────────────────────────────
  autoTable(doc, {
    ...TABLE_GRID_STYLES,
    startY: y,
    margin: { left: 14, right: 14 },
    body: [
      [
        { content: 'Contact', styles: { fontStyle: 'bold' } },
        d(f.contact),
        { content: 'Email Address', styles: { fontStyle: 'bold' } },
        d(f.email),
      ],
      [
        { content: 'Address', styles: { fontStyle: 'bold' } },
        { content: d(f.address), colSpan: 3 },
      ],
      [
        { content: 'Directions', styles: { fontStyle: 'bold' } },
        { content: d(f.direction), colSpan: 3 },
      ],
    ],
    columnStyles: {
      0: { cellWidth: 28 },
      1: { cellWidth: 72 },
      2: { cellWidth: 30 },
      // col 3: fills remaining width
    },
  });

  // ── Additional Locations / Supporting Sites ───────────────────────────────
  const locsY = sectionTitle(
    doc,
    'ADDITIONAL LOCATIONS / SUPPORTING SITES OR FUNCTIONS TO BE ON THE CERTIFICATE',
    (doc.lastAutoTable?.finalY ?? startY) + 6,
  );
  const locs = resolveCertificateLocations(f, projectData);
  const locBody = locs.map((loc, i) => [
    String(i + 1),
    [d(loc?.siteFunction || ''), loc?.address ? d(loc.address) : ''].filter((value) => value && value !== '-').join('\n'),
    d(loc?.scope || ''),
  ]);
  while (locBody.length < 3) locBody.push([String(locBody.length + 1), '-', '-']);
  autoTable(doc, {
    ...TABLE_GRID_STYLES,
    startY: locsY,
    margin: { left: 14, right: 14 },
    head: [['#', 'SITE / FUNCTION', 'SCOPE OF CERTIFICATION']],
    body: locBody,
    columnStyles: {
      0: { cellWidth: 10, halign: 'center' },
      1: { cellWidth: 75 },
    },
  });

  const soaText = firstValue(f.statementOfApplicability, f.soa);
  if (String(soaText || '').trim()) {
    autoTable(doc, {
      ...TABLE_GRID_STYLES,
      startY: (doc.lastAutoTable?.finalY ?? startY) + 5,
      margin: { left: 14, right: 14 },
      body: [
        [{ content: 'Statement of Applicability (SOA)', styles: { fontStyle: 'bold' } }, d(soaText)],
      ],
      columnStyles: { 0: { cellWidth: 58 } },
    });
  }

  // ── Exceptions / Exclusions ───────────────────────────────────────────────
  const excY = sectionTitle(
    doc,
    'EXCEPTIONS / EXCLUSION (PLEASE LIST BELOW)',
    (doc.lastAutoTable?.finalY ?? startY) + 6,
  );
  autoTable(doc, {
    ...TABLE_GRID_STYLES,
    startY: excY,
    margin: { left: 14, right: 14 },
    body: [
      [{ content: 'Requirement', styles: { fontStyle: 'bold' } }, d(f.exceptionsRequirement)],
      [{ content: 'Location', styles: { fontStyle: 'bold' } }, d(f.exceptionsLocation)],
      [{ content: 'Activities', styles: { fontStyle: 'bold' } }, d(f.exceptionsActivities)],
      [{ content: 'Additional Comment', styles: { fontStyle: 'bold' } }, d(f.exceptionsAdditionalComment)],
    ],
    columnStyles: { 0: { cellWidth: 45 } },
  });

  // ── Client and Auditor Acknowledgement ────────────────────────────────────
  const ackY = sectionTitle(
    doc,
    'CLIENT AND AUDITOR ACKNOWLEDGEMENT',
    (doc.lastAutoTable?.finalY ?? startY) + 6,
  );
  autoTable(doc, {
    ...TABLE_GRID_STYLES,
    startY: ackY,
    margin: { left: 14, right: 14 },
    body: [
      [
        { content: 'Auditor', styles: { fontStyle: 'bold' } },
        d(f.acknowledgeAuditor),
        { content: 'Date(s) of Audit', styles: { fontStyle: 'bold' } },
        fmtDate(f.acknowledgeDateOfAudit),
      ],
      [
        { content: 'Lead Auditor Signature', styles: { fontStyle: 'bold' } },
        d(f.leadAuditorSignature),
        { content: 'Client Representative', styles: { fontStyle: 'bold' } },
        d(f.clientRepresentativeName),
      ],
      [
        { content: 'Client Signature', styles: { fontStyle: 'bold' } },
        { content: d(f.clientRepresentativeSignature), colSpan: 3 },
      ],
    ],
    columnStyles: {
      0: { cellWidth: 28 },
      1: { cellWidth: 72 },
      2: { cellWidth: 32 },
    },
  });
};

const renderFAPP07 = (doc, f, startY, projectData = {}) => {
  let y = addTopMetaTable(doc, f, projectData, startY, { leftWidth: 140 });
  const structuredClauseReferences = Array.isArray(f.clauseReferences)
    ? f.clauseReferences.map((reference) => [reference?.standardId, reference?.code || reference?.label].filter(Boolean).join(' · ')).filter(Boolean).join('\n')
    : '';
  autoTable(doc, {
    ...TABLE_GRID_STYLES,
    startY: sectionTitle(doc, 'Part 1: Client Audit Data', y),
    margin: { left: 14, right: 14 },
    styles: { ...TABLE_GRID_STYLES.styles, fontSize: 6.2, cellPadding: 1.1 },
    head: [[
      'No', 'Finding Category', 'Action Required', 'Finding', 'Requirement', 'Objective Evidence',
      'Issued by', 'Correction', 'Root-Cause', 'Corrective Action Plan', 'Plan Verification',
      'Target Date', 'Acceptance / Comment', 'Accepted by', 'Accepted Date',
      'Implementation', 'Effectiveness', 'Nonconformity Closed', 'Verified By / Date',
    ]],
    body: [[
      d(f.findingNumber || f.findingReference || '1'),
      [d(f.findingType || f.findingCategory), fmtDate(f.findingDate)].filter((value) => value && value !== '-').join('\n'),
      d(f.actionRequired || f.status),
      d(f.findingDetails || f.findingDescription || f.evidenceFinding),
      [structuredClauseReferences, d(f.requirement || f.clauseReference)].filter((value) => value && value !== '-').join('\n'),
      d(f.objectiveEvidence || f.evidenceFinding),
      d(f.issuedBy || f.auditorName || getLeadAuditorName(f, projectData)),
      d(f.correction || f.auditeeResponse),
      d(f.rootCause),
      d(f.correctiveAction || f.proposedCorrectiveAction),
      d(f.planVerification),
      fmtDate(f.targetDate),
      d(f.acceptanceComment || f.remarks),
      d(f.acceptedBy),
      fmtDate(f.acceptedDate),
      d(f.correctiveActionImplemented || f.implementation),
      d(f.effectiveness),
      d(f.nonconformityClosed),
      [d(f.verifiedBy), fmtDate(f.verifiedDate)].filter((v) => v && v !== '-').join('\n'),
    ]],
    columnStyles: {
      0: { cellWidth: 8 },
      1: { cellWidth: 18 },
      2: { cellWidth: 22 },
      3: { cellWidth: 32 },
      4: { cellWidth: 26 },
      5: { cellWidth: 24 },
      6: { cellWidth: 18 },
    },
  });
};

const renderFAPP07Accepted = (doc, f, startY, projectData = {}) => renderFAPP07(doc, f, startY, projectData);

const renderFAPP08 = (doc, f, startY, projectData = {}) => {
  let y = addTopMetaTable(doc, {
    ...f,
    clientName: f.clientCompany || f.clientName,
    auditActivity: f.auditType || f.auditActivity,
  }, projectData, startY, { leftWidth: 95 });
  if (String(f.version || '').trim()) {
    autoTable(doc, {
      ...TABLE_GRID_STYLES,
      startY: y,
      margin: { left: 14, right: 14 },
      body: [[{ content: 'Version', styles: { fontStyle: 'bold' } }, d(f.version)]],
      columnStyles: { 0: { cellWidth: 35 } },
    });
    y = (doc.lastAutoTable?.finalY ?? y) + 4;
  }
  // ── Change details ────────────────────────────────────────────────────────
  const changeOptions = [
    'Legal status',
    'Organization and management',
    'Address and sites',
    'Scope of operations',
    'Management system and processes',
    'Other',
  ];
  const selectedChanges = Array.isArray(f.changesAffecting) ? f.changesAffecting : [];
  const markChange = (item) => `[${selectedChanges.some((selected) => String(selected).toLowerCase() === String(item).toLowerCase()) ? '✓' : ' '}] ${item}`;
  autoTable(doc, {
    ...TABLE_GRID_STYLES,
    startY: sectionTitle(doc, 'Changes of affecting the scope of certifications(s)', y),
    margin: { left: 14, right: 14 },
    body: [
      [
        { content: changeOptions.slice(0, 4).map(markChange).join('\n') },
        { content: changeOptions.slice(4, 7).map(markChange).join('\n') },
        { content: changeOptions.slice(7).map(markChange).join('\n') },
      ],
      [{ content: `Other change:\n${d(f.otherChange)}`, colSpan: 3 }],
      [{ content: `Previous scope:\n${d(f.previousScope || f.previouseScope)}`, colSpan: 3, styles: { minCellHeight: 18 } }],
      [{ content: `New Scope:\n${d(f.newScope || f.changeDescription)}`, colSpan: 3, styles: { minCellHeight: 18 } }],
      [{ content: `Notification to authorities / interested parties:\n${d(f.notificationAuthorities)}`, colSpan: 3 }],
      [{ content: `Results of review / decision:\n${d(f.resultsReviewDecision)}`, colSpan: 3, styles: { minCellHeight: 16 } }],
    ],
  });

  // ── Authorisation ─────────────────────────────────────────────────────────
  autoTable(doc, {
    ...TABLE_GRID_STYLES,
    startY: (doc.lastAutoTable?.finalY ?? startY) + 5,
    margin: { left: 14, right: 14 },
    body: [
      [{ content: 'Administrative changes', colSpan: 4, styles: { fillColor: COLOR_SECTION, textColor: 0 } }],
      [{ content: '[ ] Voluntary withdrawal / transfer of certification', colSpan: 4 }],
      [{ content: '[ ] Non-renewal of certification', colSpan: 4 }],
      [
        { content: 'Requested By', styles: { fontStyle: 'bold' } },
        d(f.requestedBy),
        { content: 'Effective Date', styles: { fontStyle: 'bold' } },
        fmtDate(f.effectiveDate || f.noticeDate),
      ],
      [
        { content: 'Approved By', styles: { fontStyle: 'bold' } },
        d(f.approvedBy),
        { content: 'Remarks', styles: { fontStyle: 'bold' } },
        d(f.remarks || f.impactAssessment),
      ],
      [
        { content: 'Originator', styles: { fontStyle: 'bold' } }, d(f.originator),
        { content: 'Completed Administratively By', styles: { fontStyle: 'bold' } }, d(f.completedAdministrativelyBy),
      ],
      [
        { content: 'Approved by Technical Manager', styles: { fontStyle: 'bold' } },
        { content: d(f.approvedTechnicalManagerBy), colSpan: 3 },
      ],
    ],
    columnStyles: {
      0: { cellWidth: 32 },
      1: { cellWidth: 65 },
      2: { cellWidth: 30 },
    },
  });
};

const renderFTRD01 = (doc, f, startY, projectData = {}) => {
  // Helper: map value to tick mark in the correct column (YES | NO | N/A | details)
  const checkRow = (label, obj) => {
    const v = String(obj?.value || '').toUpperCase().trim();
    return [
      label,
      v === 'YES' ? '✓' : '',
      v === 'NO' ? '✓' : '',
      v === 'N/A' ? '✓' : '',
      d(obj?.details),
    ];
  };

  const p1 = f.part1 || {};
  const p2 = f.part2 || {};
  const p3 = f.part3 || {};

  let y = addTopMetaTable(doc, f, projectData, startY, { compact: true, leftWidth: 92 });
  y = addMultiAuditCriteriaNote(doc, y);
  const headStyles = { fillColor: [255, 255, 255], textColor: 0, fontStyle: 'bold', fontSize: 7, lineColor: COLOR_GRID, lineWidth: 0.12 };
  const colStyles = {
    0: { cellWidth: 88 },
    1: { cellWidth: 11, halign: 'center', fontStyle: 'bold' },
    2: { cellWidth: 11, halign: 'center', fontStyle: 'bold' },
    3: { cellWidth: 11, halign: 'center', fontStyle: 'bold' },
    // col 4 (details): fills remaining width
  };
  const tableStyles = { fontSize: 7, cellPadding: 1.2, lineColor: COLOR_GRID, lineWidth: 0.12, textColor: 0 };

  // ── Part 1: Technical Review & Items Reviewed ─────────────────────────────
  autoTable(doc, {
    startY: y,
    margin: { left: 14, right: 14 },
    theme: 'grid',
    styles: tableStyles,
    head: [['PART 1 — TECHNICAL REVIEW & ITEMS REVIEWED', 'YES', 'NO', 'N/A', 'DETAILS / COMMENTS BY TECH. REVIEWER']],
    body: [
      checkRow('1. Package complete as per FAPP-05 — Audit Report', p1.packageComplete),
      checkRow('2. Technical Review waived', p1.technicalReviewWaived),
      checkRow('3. Audit man-days executed as per approved assignment document', p1.auditMandaysExecuted),
      checkRow('4. Audit team assigned as per approved assignment document', p1.auditTeamAssigned),
      checkRow('01 — Grading of findings', p1.gradingOfFindings),
      checkRow('02 — Writing of findings', p1.writingOfFindings),
      checkRow('03 — Verification of Nonconformities', p1.verificationOfNC),
      checkRow('04 — Completeness of the recertification audit (R)', p1.completenessRecertification),
      checkRow('05 — Audit records', p1.auditRecords),
      checkRow('06 — Validity of certification', p1.validityOfCertification),
      checkRow('07 — Audit of mandatory elements', p1.auditMandatoryElements),
      checkRow('08 — Audit Planning', p1.auditPlanning),
      checkRow('10 — Exclusions', p1.exclusions),
    ],
    headStyles,
    columnStyles: colStyles,
  });

  // ── Part 2: Corrective Action Review & Approval of Recommendation ─────────
  autoTable(doc, {
    startY: (doc.lastAutoTable?.finalY ?? startY) + 4,
    margin: { left: 14, right: 14 },
    theme: 'grid',
    styles: tableStyles,
    head: [['PART 2 — CORRECTIVE ACTION REVIEW & APPROVAL OF RECOMMENDATION', 'YES', 'NO', 'N/A', 'DETAILS / COMMENTS']],
    body: [
      checkRow('1. Corrective action plans accepted (minor NCs)', p2.capMinorAccepted),
      checkRow('2. Corrective actions accepted (Major NCs)', p2.caMajorAccepted),
      checkRow("3. Approval of Lead Auditor's recommendation", p2.approvalLeadAuditor),
    ],
    headStyles,
    columnStyles: colStyles,
  });

  // ── Part 3: Certification Authority Review ────────────────────────────────
  autoTable(doc, {
    startY: (doc.lastAutoTable?.finalY ?? startY) + 4,
    margin: { left: 14, right: 14 },
    theme: 'grid',
    styles: tableStyles,
    head: [['PART 3 — CERTIFICATION AUTHORITY REVIEW', 'YES', 'NO', 'N/A', 'DETAILS / COMMENTS']],
    body: [
      checkRow('1. Acceptability of results of the above', p3.acceptabilityOfResults),
      checkRow('2. Re-certification audits (review of entire cycle prior to planning)', p3.recertificationAudits),
      checkRow('3. Current certification still valid', p3.currentCertValid),
      checkRow("4. Lead Auditor's recommendation approved", p3.leadAuditorApproved),
    ],
    headStyles,
    columnStyles: colStyles,
  });
};

const renderFTRD02 = (doc, f, startY, projectData = {}) => {
  let tableEndY = addTopMetaTable(doc, f, projectData, startY, { compact: true, leftWidth: 92 });
  tableEndY = addMultiAuditCriteriaNote(doc, tableEndY);

  // ── Section 1: Certificate Core ───────────────────────────────────────────
  const s1Y = sectionTitle(doc, 'Part 1 - Certificate Production (Use of this section / part is applicable only if certificate is produced)', tableEndY);
  const checkRow = (label, value, comment = '') => {
    const v = String(value || '').toUpperCase().trim();
    return [label, v === 'YES' ? 'x' : '', v === 'NO' ? 'x' : '', v === 'N/A' ? 'x' : '', comment];
  };
  autoTable(doc, {
    ...TABLE_GRID_STYLES,
    startY: s1Y,
    margin: { left: 14, right: 14 },
    head: [['', 'Yes', 'No', 'N/A', 'Comment']],
    body: [
      checkRow('Audit Report or Certificate Information Form (CIF) received (signed)', f.auditReportReceived || 'YES'),
      checkRow('Change Notice Form received (if applicable)', f.changeNoticeReceived || 'N/A'),
      checkRow('Change Notice entered / uploaded to Audit-Q', f.changeNoticeUploaded || 'N/A'),
      checkRow('Writing the company name and address according to the Certificate Information Form (CIF) signed', f.companyNameAddressWritten || 'YES'),
      checkRow('Writing the scope according to SOA documents and with Certificate Information Form (CIF) signed', f.scopeWritten || 'YES'),
      checkRow('The certificate number, initial certification date, issue date, and expired date are correct', f.certificateDatesCorrect || 'YES'),
      checkRow('Barcodes and links can be scanned, and the data can be matched with the certificate', f.barcodesValid || 'YES'),
      checkRow('Certification authority approval obtained', f.authorityApproval || 'YES'),
      [{ content: 'New certificate created', styles: { fontStyle: 'bold' } }, d(f.newCertificateCreated || 'x'), '', '', d(f.certificateNumber)],
      [{ content: 'Certificate Production Personnel:', styles: { fontStyle: 'bold' } }, { content: d(f.producedBy), colSpan: 2 }, { content: 'Date:', styles: { fontStyle: 'bold' } }, fmtDate(f.issueDate || f.validFrom)],
    ],
    columnStyles: {
      0: { cellWidth: 78 },
      1: { cellWidth: 35, halign: 'center' },
      2: { cellWidth: 35, halign: 'center' },
      3: { cellWidth: 35, halign: 'center' },
    },
  });
  tableEndY = doc.lastAutoTable?.finalY ?? tableEndY;

  // ── Section 2: Scope Verification ─────────────────────────────────────────
  if (String(f.verificationOfScope || '').trim()) {
    const s2Y = sectionTitle(doc, 'SCOPE VERIFICATION', tableEndY + 6);
    autoTable(doc, {
      startY: s2Y,
      margin: { left: 14, right: 14 },
      theme: 'grid',
      styles: { fontSize: 8.5, cellPadding: 2.5 },
      body: [
        [{ content: 'Verification of Scope', styles: { fontStyle: 'bold', fillColor: [240, 248, 248] } }, d(f.verificationOfScope)],
      ],
      columnStyles: { 0: { cellWidth: 50 } },
    });
    tableEndY = doc.lastAutoTable?.finalY ?? tableEndY;
  }

  // ── Section 3: Validity Timeline ──────────────────────────────────────────
  const s3Y = sectionTitle(doc, 'VALIDITY TIMELINE', tableEndY + 6);
  autoTable(doc, {
    startY: s3Y,
    margin: { left: 14, right: 14 },
    theme: 'grid',
    styles: { fontSize: 8.5, cellPadding: 2 },
    head: [['INITIAL DATE', 'LATEST CERT. DATE', 'ISSUE DATE', 'EXPIRED DATE', 'VALID FROM', 'VALID TO']],
    body: [[
      fmtDate(f.initialDate),
      fmtDate(f.latestCertificationDate),
      fmtDate(f.issueDate),
      fmtDate(f.expiredDate),
      fmtDate(f.validFrom),
      fmtDate(f.validTo),
    ]],
    headStyles: { fillColor: COLOR_TEAL, textColor: 255, fontStyle: 'bold', fontSize: 8 },
    columnStyles: {
      0: { halign: 'center' },
      1: { halign: 'center' },
      2: { halign: 'center' },
      3: { halign: 'center' },
      4: { halign: 'center' },
      5: { halign: 'center' },
    },
  });
  tableEndY = doc.lastAutoTable?.finalY ?? tableEndY;

  // ── Section 4: Issuance Roles ─────────────────────────────────────────────
  const s4Y = sectionTitle(doc, 'ISSUANCE ROLES', tableEndY + 6);
  autoTable(doc, {
    startY: s4Y,
    margin: { left: 14, right: 14 },
    theme: 'grid',
    styles: { fontSize: 8.5, cellPadding: 2 },
    head: [['PRODUCED BY', 'REVIEWED BY', 'APPROVED BY']],
    body: [[d(f.producedBy), d(f.reviewedBy), d(f.approvedBy)]],
    headStyles: { fillColor: COLOR_TEAL, textColor: 255, fontStyle: 'bold' },
  });
  tableEndY = doc.lastAutoTable?.finalY ?? tableEndY;

  if (String(f.remarks || '').trim()) {
    autoTable(doc, {
      startY: tableEndY + 4,
      margin: { left: 14, right: 14 },
      theme: 'grid',
      styles: { fontSize: 8.5, cellPadding: 2.5 },
      body: [
        [{ content: 'Remarks', styles: { fontStyle: 'bold', fillColor: [240, 248, 248] } }, d(f.remarks)],
      ],
      columnStyles: { 0: { cellWidth: 25 } },
    });
  }
};

const renderSummary = (doc, f, startY, projectData = {}) => {
  const W = doc.internal.pageSize.getWidth();
  doc.setTextColor(...COLOR_SECTION);
  doc.setFont(undefined, 'bold');
  doc.setFontSize(22);
  doc.text('AUDIT SUMMARY', W / 2, 40, { align: 'center' });
  doc.setTextColor(0, 0, 0);
  doc.setFont(undefined, 'normal');
  doc.setFontSize(16);
  doc.text(d(getClientName(f, projectData)), W / 2, 62, { align: 'center' });
  doc.setFontSize(15);
  doc.text(d(getAuditCriteria(f, projectData)), W / 2, 86, { align: 'center' });
  doc.setFontSize(12);
  doc.text(d(f.managementSystem || projectData?.scopeLabel || projectData?.scope), W / 2, 98, { align: 'center' });
  doc.setFontSize(7);
  doc.text(d(getLeadAuditorName(f, projectData)), W - 68, 116);
  doc.text(d(f.secondAuditor || f.auditTeam?.[1]?.name || ''), W - 68, 121);

  doc.addPage();
  let tableEndY = startY;

  // ── Audit metadata ────────────────────────────────────────────────────────
  autoTable(doc, {
    ...TABLE_GRID_STYLES,
    startY: tableEndY,
    margin: { left: 14, right: 14 },
    body: [
      [
        { content: 'Audit Date', styles: { fontStyle: 'bold', fillColor: [240, 248, 248] } },
        fmtDate(f.auditDate),
        { content: 'Lead Auditor', styles: { fontStyle: 'bold', fillColor: [240, 248, 248] } },
        d(f.auditLeadId),
      ],
      [
        { content: 'Audit Team', styles: { fontStyle: 'bold', fillColor: [240, 248, 248] } },
        {
          content: (f.auditTeam || []).map((m) => d(m.name)).filter((v) => v !== '-').join(', ') || '-',
          colSpan: 3,
        },
      ],
      [
        { content: 'Conclusion', styles: { fontStyle: 'bold', fillColor: [240, 248, 248] } },
        { content: d(f.conclusion), colSpan: 3 },
      ],
    ],
    columnStyles: {
      0: { cellWidth: 30 },
      1: { cellWidth: 65 },
      2: { cellWidth: 28 },
      // col 3: fills remaining width
    },
  });
  tableEndY = doc.lastAutoTable?.finalY ?? tableEndY;

  // ── Findings overview ─────────────────────────────────────────────────────
  const foY = sectionTitle(doc, 'FINDINGS OVERVIEW', tableEndY + 6);
  autoTable(doc, {
    ...TABLE_GRID_STYLES,
    startY: foY,
    margin: { left: 14, right: 14 },
    head: [['MAJOR NC', 'MINOR NC', 'OBSERVATIONS', 'CONFORMITIES']],
    body: [[d(f.majorNC ?? 0), d(f.minorNC ?? 0), d(f.observations ?? 0), d(f.conformities ?? 0)]],
    columnStyles: {
      0: { halign: 'center' },
      1: { halign: 'center' },
      2: { halign: 'center' },
      3: { halign: 'center' },
    },
  });
  tableEndY = doc.lastAutoTable?.finalY ?? tableEndY;

  // ── Recommendations ───────────────────────────────────────────────────────
  if (String(f.recommendations || '').trim()) {
    autoTable(doc, {
      startY: tableEndY + 4,
      margin: { left: 14, right: 14 },
      theme: 'grid',
      styles: { fontSize: 8.5, cellPadding: 2.5 },
      head: [['RECOMMENDATIONS']],
      body: [[d(f.recommendations)]],
      headStyles: { fillColor: COLOR_TEAL, textColor: 255, fontStyle: 'bold' },
    });
    tableEndY = doc.lastAutoTable?.finalY ?? tableEndY;
  }

  // ── Summary narrative ─────────────────────────────────────────────────────
  if (String(f.summaryNarrative || '').trim()) {
    const snY = sectionTitle(doc, 'SUMMARY NARRATIVE', tableEndY + 6);
    autoTable(doc, {
      startY: snY,
      margin: { left: 14, right: 14 },
      theme: 'grid',
      styles: { fontSize: 8.5, cellPadding: 3 },
      body: [[d(f.summaryNarrative)]],
    });
  }
};

const RENDERERS = {
  'FAPP-01': renderFAPP01,
  'FAPP-02': renderFAPP02,
  'FAPP-03': renderFAPP03,
  'FAPP-04': renderFAPP04,
  'FAPP-05': renderFAPP05,
  'FAPP-06': renderFAPP06,
  'FAPP-07': renderFAPP07,
  'FAPP-08': renderFAPP08,
  'FTRD-01': renderFTRD01,
  'FTRD-02': renderFTRD02,
  'SUMMARY': renderSummary,
  'FSCP-03': (doc, f, startY, projectData = {}) => {
    const left = 14; const right = 196; let y = 22;
    if (projectData?.branding?.logo) doc.addImage(projectData.branding.logo, 'PNG', left, 12, 36, 22);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.text(d(f.place || 'Jakarta'), right - 2, 42, { align: 'right' });
    y = 62; const recipient = `${f.salutation || 'Mr.'} ${f.recipient || ''}`.trim();
    const company = firstValue(f.clientCompany, f.companyName, projectData.companyName, projectData.clientName);
    const address = firstValue(f.clientAddress, projectData.clientAddress, projectData.address);
    doc.text('To:', left, y); y += 7; doc.text(recipient, left, y); y += 7;
    if (company) { doc.setFont('helvetica', 'bold'); doc.text(company, left, y); doc.setFont('helvetica', 'normal'); y += 7; }
    if (address) { doc.text(doc.splitTextToSize(address, 175), left, y); y += 12; }
    doc.setFont('helvetica', 'underline'); doc.text(`Subject: ${firstValue(f.subject, `Audit ${d(f.auditType)}`)}`, left, y); doc.setFont('helvetica', 'normal'); y += 14;
    doc.text(`Dear ${recipient},`, left, y); y += 14;
    const paragraphs = [
      'We would like to inform you that the audit will be conducted as follows:',
      `- Audit '${d(f.auditType)}' on ${d(f.conductedOn)}`,
      `Scope: "${d(f.scope)}"`,
      `We will appreciate your confirmation by signing this confirmation letter and return by fax or by email latest by ${fmtDateNative(f.confirmationDueDate)}.`,
      `If you postpone the services with less than ${d(f.lessThanWorkingDays)} working days' notice CBQA Global reserves the right to charge a fee amounting to the greater of ${d(f.chargePercentage)}% of the quoted Services Fee or one man-day at the current rate of Rp ${d(f.servicesFee)} (as stated in the Certification and Service Agreement point ${d(f.serviceAgreementPoint)} Postponement).`,
      `In the meantime should there be any questions or if we can be of any assistance to you in regard to the above please feel free to contact us or email to ${d(f.emailQuestions)}`,
      `Should your company have any feedback related to our services please contact our office by sending an e-mail at ${d(f.emailFeedback)}`,
      'Thank you very much for your kind attention and we will look forward to your response.',
      'Sincerely yours,',
    ];
    doc.setFontSize(10.5); paragraphs.forEach((paragraph, index) => { const lines = doc.splitTextToSize(paragraph, right - left); doc.text(lines, left, y); y += lines.length * 5 + (index === 1 || index === 2 ? 7 : 8); });
    doc.setFont('helvetica', 'bold'); doc.text('PT CBQA GLOBAL INDONESIA', left, y + 4); doc.text(company || 'CLIENT', 105, y + 4); doc.setFont('helvetica', 'normal');
    doc.text('(Authorized Representative)', left, y + 39); doc.text(`(${recipient})`, 135, y + 39);
    doc.text('South Quarter Tower A 17th Floor Unit B2-C1', left, y + 47); doc.text('+62 21 2781 4200', right, y + 47, { align: 'right' });
    doc.text('Jl. RA. Kartini Kav. 8, Cilandak, Jakarta Selatan', left, y + 53); doc.text('www.cbqaglobal.com', right, y + 53, { align: 'right' });
  },
};

export const exportFormToPDF = async (formData, formType, projectData = {}) => {
  const rawCode = formType?.code || 'AUDIT-FORM';
  const code = normalizeDocCode(rawCode);

  // Special case: FAPP-07 accepted uses its own renderer
  const resolvedCode = rawCode.includes('accepted') ? 'FAPP-07-ACCEPTED' : code;

  const doc = new jsPDF(auditQDocOptions(resolvedCode));
  const [logoResult, signatureResult] = await Promise.allSettled([
    loadImageAsDataUrl('/static/logo.png'),
    loadImageAsDataUrl('/static/signature.png'),
  ]);
  const branding = {
    logo: logoResult.status === 'fulfilled' ? logoResult.value : null,
    signature: signatureResult.status === 'fulfilled' ? signatureResult.value : null,
  };

  if (resolvedCode === 'FAPP-01') {
    await renderFAPP01(doc, formData, { ...projectData, branding });
  } else {
    const startY = FORM_TOP_Y;

    if (resolvedCode === 'FAPP-07-ACCEPTED') {
      renderFAPP07Accepted(doc, formData, startY, projectData);
    } else if (RENDERERS[resolvedCode]) {
      RENDERERS[resolvedCode](doc, formData, startY, { ...projectData, branding });
    } else {
      // Generic fallback: flat key-value table
      const rows = Object.entries(formData || {}).map(([k, v]) => [k, d(v)]);
      autoTable(doc, {
        ...TABLE_GRID_STYLES,
        startY,
        margin: { left: 14, right: 14 },
        head: [['Field', 'Value']],
        body: rows.length ? rows : [['Info', 'No data']],
        columnStyles: { 0: { cellWidth: 60 } },
      });
    }

    if (resolvedCode === 'SUMMARY') {
      addSummaryChrome(doc, formData, projectData, branding);
    } else if (resolvedCode === 'FSCP-03') {
      // Confirmation Letter is a client-facing letterhead, not an AuditQ form sheet.
    } else {
      addAuditQPageChrome(doc, {
        title: FORM_TITLES[resolvedCode] || rawCode,
        footerCode: FORM_FOOTER_CODES[resolvedCode] || rawCode,
        branding,
        documentNumber: formData?.documentMetadata?.documentNumber || (resolvedCode === 'FAPP-04' ? 'FAPP-04' : ''),
        releaseDate: resolvedCode === 'FAPP-04' ? '01-Jun-2019' : '',
      });
    }
  }

  const exportSuffix = projectData?.exportSuffix
    ? `_${String(projectData.exportSuffix).replace(/[^A-Za-z0-9_-]/g, '_')}`
    : '';
  const fileName = resolvedCode === 'FAPP-01'
    ? `Form_Audit_Plan_AP-${projectData?.exportId || projectData?.projectId || Date.now()}${exportSuffix}.pdf`
    : `${rawCode.replace(/[^A-Za-z0-9_-]/g, '_')}${projectData?.projectId ? `_${projectData.projectId}` : ''}${exportSuffix}_${fmtDateForFile()}.pdf`;
  doc.save(fileName);
};

export default exportFormToPDF;
